import axios from "axios";
import { useEffect, useState, useCallback } from "react";
import { Loader2, Trello, GripVertical, Mail, Phone } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/context/AuthContext";
import ScoreIndicator from "@/components/ScoreIndicator";
import { CandidateOriginBadge } from "@/lib/candidateOrigin";

/** Tailwind classes per column color. Defined statically so JIT keeps them.
 * Each palette covers: solid bar, soft chip, hover ring, AND tinted column bg.
 */
const COLUMN_COLORS = {
  slate:   { bar: "bg-slate-400",   chip: "bg-slate-100 text-slate-700",   ring: "ring-slate-300",   colBg: "bg-slate-50/80",   header: "bg-slate-100/60",   border: "border-slate-200" },
  blue:    { bar: "bg-blue-500",    chip: "bg-blue-100 text-blue-800",     ring: "ring-blue-300",    colBg: "bg-blue-50/70",    header: "bg-blue-100/50",    border: "border-blue-200" },
  violet:  { bar: "bg-violet-500",  chip: "bg-violet-100 text-violet-800", ring: "ring-violet-300",  colBg: "bg-violet-50/70",  header: "bg-violet-100/50",  border: "border-violet-200" },
  amber:   { bar: "bg-amber-500",   chip: "bg-amber-100 text-amber-800",   ring: "ring-amber-300",   colBg: "bg-amber-50/70",   header: "bg-amber-100/60",   border: "border-amber-200" },
  emerald: { bar: "bg-emerald-500", chip: "bg-emerald-100 text-emerald-800", ring: "ring-emerald-300", colBg: "bg-emerald-50/70", header: "bg-emerald-100/50", border: "border-emerald-200" },
  rose:    { bar: "bg-rose-500",    chip: "bg-rose-100 text-rose-800",     ring: "ring-rose-300",    colBg: "bg-rose-50/70",    header: "bg-rose-100/50",    border: "border-rose-200" },
  indigo:  { bar: "bg-indigo-500",  chip: "bg-indigo-100 text-indigo-800", ring: "ring-indigo-300",  colBg: "bg-indigo-50/70",  header: "bg-indigo-100/50",  border: "border-indigo-200" },
  sky:     { bar: "bg-sky-500",     chip: "bg-sky-100 text-sky-800",       ring: "ring-sky-300",     colBg: "bg-sky-50/70",     header: "bg-sky-100/50",     border: "border-sky-200" },
  lime:    { bar: "bg-lime-500",    chip: "bg-lime-100 text-lime-800",     ring: "ring-lime-300",    colBg: "bg-lime-50/70",    header: "bg-lime-100/50",    border: "border-lime-200" },
  fuchsia: { bar: "bg-fuchsia-500", chip: "bg-fuchsia-100 text-fuchsia-800", ring: "ring-fuchsia-300", colBg: "bg-fuchsia-50/70", header: "bg-fuchsia-100/50", border: "border-fuchsia-200" },
  teal:    { bar: "bg-teal-500",    chip: "bg-teal-100 text-teal-800",     ring: "ring-teal-300",    colBg: "bg-teal-50/70",    header: "bg-teal-100/50",    border: "border-teal-200" },
  orange:  { bar: "bg-orange-500",  chip: "bg-orange-100 text-orange-800", ring: "ring-orange-300",  colBg: "bg-orange-50/70",  header: "bg-orange-100/50",  border: "border-orange-200" },
};

const KanbanCard = ({ c, onOpen, draggable, onDragStart, onDragEnd, dragging }) => {
  const initials = (c.name || "?")
    .split(" ")
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <div
      data-testid={`kanban-card-${c.id}`}
      draggable={draggable}
      onDragStart={(e) => onDragStart?.(e, c)}
      onDragEnd={onDragEnd}
      onClick={() => onOpen(c)}
      className={`group bg-white border border-slate-200 rounded-lg p-3 cursor-pointer transition-all hover:shadow-md hover:border-slate-300 ${
        dragging ? "opacity-40 scale-95" : ""
      }`}
    >
      <div className="flex items-start gap-2">
        <GripVertical className="w-3.5 h-3.5 text-slate-300 mt-1 shrink-0 group-hover:text-slate-500" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 mb-1.5">
            <h4
              className="text-sm font-semibold text-slate-900 leading-tight truncate flex-1 min-w-0"
              title={c.name}
            >
              {c.name || "—"}
            </h4>
            <ScoreIndicator
              score={c.ranking_score ?? c.score ?? 0}
              compact
            />
          </div>
          <div className="flex flex-col gap-0.5 text-[11px] text-slate-500">
            <CandidateOriginBadge candidate={c} className="self-start mb-1" />
            {c.email && (
              <span
                className="inline-flex items-center gap-1 truncate"
                title={c.email}
              >
                <Mail className="w-3 h-3 shrink-0" />
                <span className="truncate">{c.email}</span>
              </span>
            )}
            {c.phone && (
              <span className="inline-flex items-center gap-1">
                <Phone className="w-3 h-3 shrink-0" />
                {c.phone}
              </span>
            )}
            {c.experience_years > 0 && (
              <span className="text-slate-400 italic">
                {c.experience_years} anos de experiência
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const JobKanban = ({ jobId, onCandidateClick, refreshSignal }) => {
  const [columns, setColumns] = useState([]);
  const [byStatus, setByStatus] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [draggingId, setDraggingId] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/jobs/${jobId}/kanban`);
      setColumns(res.data.columns || []);
      setByStatus(res.data.candidates_by_status || {});
    } catch (err) {
      setError(
        err?.response?.data?.detail || "Erro ao carregar o quadro de candidatos."
      );
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => {
    load();
  }, [load, refreshSignal]);

  const handleDragStart = (e, cand) => {
    setDraggingId(cand.id);
    e.dataTransfer.effectAllowed = "move";
    // Some browsers require setData to actually allow the drop
    e.dataTransfer.setData("text/plain", cand.id);
  };

  const handleDragEnd = () => {
    setDraggingId(null);
    setDropTarget(null);
  };

  const handleDragOver = (e, columnKey) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dropTarget !== columnKey) setDropTarget(columnKey);
  };

  const handleDragLeave = (e, columnKey) => {
    // Only clear if leaving the column for real (not crossing inner children)
    if (e.currentTarget === e.target && dropTarget === columnKey) {
      setDropTarget(null);
    }
  };

  const moveCandidate = async (candId, fromStatus, toStatus) => {
    if (!candId || fromStatus === toStatus) return;
    // Optimistic UI: move the card immediately, rollback on failure
    const fromList = byStatus[fromStatus] || [];
    const toList = byStatus[toStatus] || [];
    const moving = fromList.find((c) => c.id === candId);
    if (!moving) return;
    const next = { ...moving, status: toStatus };

    setByStatus((prev) => ({
      ...prev,
      [fromStatus]: (prev[fromStatus] || []).filter((c) => c.id !== candId),
      [toStatus]: [next, ...(prev[toStatus] || [])],
    }));
    setColumns((cols) =>
      cols.map((c) => {
        if (c.key === fromStatus) return { ...c, count: Math.max(0, c.count - 1) };
        if (c.key === toStatus) return { ...c, count: c.count + 1 };
        return c;
      })
    );

    try {
      await axios.patch(`${API}/candidates/${candId}/status`, {
        status: toStatus,
      });
      toast.success(
        `Movido para "${columns.find((c) => c.key === toStatus)?.label || toStatus}"`
      );
      // For rejection transitions, hint at the auto-email
      if (toStatus === "rejeitado" && (next.email || "").trim()) {
        toast.info(
          "E-mail de rejeição será enviado automaticamente em 3 dias.",
          { duration: 5000 }
        );
      }
    } catch (err) {
      // Rollback
      setByStatus((prev) => ({
        ...prev,
        [fromStatus]: [moving, ...(prev[fromStatus] || [])],
        [toStatus]: (prev[toStatus] || []).filter((c) => c.id !== candId),
      }));
      setColumns((cols) =>
        cols.map((c) => {
          if (c.key === fromStatus) return { ...c, count: c.count + 1 };
          if (c.key === toStatus) return { ...c, count: Math.max(0, c.count - 1) };
          return c;
        })
      );
      toast.error(err?.response?.data?.detail || "Erro ao mover candidato");
    }
  };

  const handleDrop = async (e, toStatus) => {
    e.preventDefault();
    const candId = e.dataTransfer.getData("text/plain") || draggingId;
    setDropTarget(null);
    setDraggingId(null);
    if (!candId) return;
    // Discover current status by inspecting state
    let fromStatus = null;
    for (const [k, list] of Object.entries(byStatus)) {
      if (list.some((c) => c.id === candId)) {
        fromStatus = k;
        break;
      }
    }
    if (!fromStatus) return;
    await moveCandidate(candId, fromStatus, toStatus);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10 text-slate-500">
        <Loader2 className="w-5 h-5 animate-spin mr-2" />
        Carregando quadro de candidatos…
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md p-4">
        {error}
      </div>
    );
  }

  return (
    <section
      data-testid="job-kanban"
      className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm"
    >
      <div className="flex items-center justify-between mb-4">
        <h2 className="heading text-lg font-semibold text-slate-900 inline-flex items-center gap-2">
          <Trello className="w-5 h-5 text-brand-blue" />
          Pipeline (Kanban)
        </h2>
        <p className="text-xs text-slate-500 hidden sm:block">
          Arraste os cards entre as colunas para alterar o status.
        </p>
      </div>

      <div className="flex gap-3 overflow-x-auto pb-3">
        {columns.map((col) => {
          const palette = COLUMN_COLORS[col.color] || COLUMN_COLORS.slate;
          const isDropTarget = dropTarget === col.key;
          const cards = byStatus[col.key] || [];
          return (
            <div
              key={col.key}
              data-testid={`kanban-column-${col.key}`}
              onDragOver={(e) => handleDragOver(e, col.key)}
              onDragLeave={(e) => handleDragLeave(e, col.key)}
              onDrop={(e) => handleDrop(e, col.key)}
              className={`flex flex-col min-w-[260px] w-[260px] rounded-lg border ${palette.colBg} ${palette.border} ${
                isDropTarget ? `ring-2 ${palette.ring}` : ""
              } transition-all`}
            >
              {/* Column header */}
              <div className={`px-3 pt-3 pb-2 border-b ${palette.border} ${palette.header} rounded-t-lg`}>
                <div className="flex items-center gap-2">
                  <span className={`block w-1.5 h-5 rounded-full ${palette.bar}`} />
                  <span className="text-sm font-semibold text-slate-800 flex-1 truncate">
                    {col.label}
                  </span>
                  <span
                    className={`inline-flex items-center justify-center min-w-[24px] h-5 px-1.5 rounded-full text-[11px] font-bold ${palette.chip}`}
                  >
                    {col.count}
                  </span>
                </div>
              </div>

              {/* Cards area */}
              <div className="p-2.5 flex-1 space-y-2 min-h-[120px] max-h-[520px] overflow-y-auto">
                {cards.length === 0 ? (
                  <div
                    className={`flex items-center justify-center h-24 text-[11px] text-slate-400 italic rounded-md border-2 border-dashed ${
                      isDropTarget
                        ? "border-slate-400 bg-white"
                        : "border-slate-200"
                    }`}
                  >
                    {isDropTarget ? "Solte aqui" : "Sem candidatos"}
                  </div>
                ) : (
                  cards.map((c) => (
                    <KanbanCard
                      key={c.id}
                      c={c}
                      onOpen={onCandidateClick}
                      draggable
                      onDragStart={handleDragStart}
                      onDragEnd={handleDragEnd}
                      dragging={draggingId === c.id}
                    />
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default JobKanban;
